import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from "react";

import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import {
  CalendarBlankIcon,
  CameraSlashIcon,
  ChalkboardTeacherIcon,
  CheckCircleIcon,
  ClockIcon,
  StudentIcon,
  StopIcon,
  WarningCircleIcon,
  XCircleIcon,
  type IconProps,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

import { LESSON_JOIN_GRACE_PERIOD, TUTRED_CLIENT_HOSTNAME } from "@/constants";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";

import VideoControls from "../VideoControls";

const VIDEO_MAX_WIDTH = 640;

function LessonDetailRow({
  icon: Icon,
  label,
  value,
  iconColor,
  valueVariant = "text18B",
}: {
  icon: ComponentType<IconProps>;
  label: string;
  value: string | undefined;
  iconColor: string;
  valueVariant?: "text16B" | "text18B";
}) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={1.5}>
      <Icon
        size={22}
        weight="duotone"
        color={iconColor}
        style={{ marginTop: 2 }}
        aria-hidden
      />
      <Box>
        <Typography variant="text15L" color="textSecondary">
          {label}
        </Typography>
        <Typography variant={valueVariant}>{value}</Typography>
      </Box>
    </Stack>
  );
}

const Lobby = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { authenticatedAxios } = useFirebaseContext();
  const { lesson } = useLesson();

  const [loading, setLoading] = useState(true);
  const [statusTitle, setStatusTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [enableJoin, setEnableJoin] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [permissionDeniedMessage, setPermissionDeniedMessage] = useState("");

  useEffect(() => {
    setLoading(false);

    if (lesson) {
      const now = new Date();
      const lessonStart = new Date(lesson.startDateTime.toDate());
      lessonStart.setMinutes(
        lessonStart.getMinutes() - LESSON_JOIN_GRACE_PERIOD
      );

      if (lesson.isCancelled) {
        setStatusTitle("Lesson has been cancelled");
        setEnableJoin(false);
        return;
      }

      if (lesson.hasCompleted) {
        setStatusTitle("Lesson has Ended");
        setEnableJoin(false);
        return;
      }

      if (now > lessonStart) {
        setStatusTitle("Lesson has started");
        setEnableJoin(true);
      } else {
        setStatusTitle("Wait for lesson to start");
        setEnableJoin(false);
      }
    }
  }, [lesson]);

  const statusVisual = useMemo(() => {
    if (enableJoin) {
      return {
        Icon: CheckCircleIcon,
        color: theme.palette.additionalGreen.main,
      };
    }
    if (statusTitle.includes("cancelled")) {
      return {
 Icon: XCircleIcon,
color: theme.palette.error.main 
};
    }
    if (statusTitle.includes("Ended")) {
      return {
 Icon: StopIcon,
color: theme.palette.text.secondary 
};
    }
    return {
 Icon: ClockIcon,
color: theme.palette.text.secondary 
};
  }, [enableJoin, statusTitle, theme.palette]);

  const handleJoin = () => {
    if (!lesson) return;
    setLoading(true);
    authenticatedAxios
      .get(`lessons/join/${lesson.id}`)
      .then((res) => {
        if (res.status === 200) {
          sessionStorage.setItem("initialMicEnabled", isMicEnabled.toString());
          sessionStorage.setItem(
            "initialCameraEnabled",
            isCameraEnabled.toString()
          );
          navigate(`lesson`, {
            state: {
              roomToken: res.data.token,
            },
          });
        }
      })
      .catch(() => {
        setLoading(false);
        setErrorMessage("Error joining lesson");
      });
  };

  const getCameraStream = useCallback(async () => {
    if (!isCameraEnabled) {
      // Stop the current stream if camera is toggled off
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    navigator.mediaDevices
      .getUserMedia({
        video: true,
        audio: true,
      })
      .then((stream) => {
        setPermissionDeniedMessage("");
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        return stream;
      })
      .catch(async (err) => {
        if (err.name === "NotAllowedError") {
          setPermissionDeniedMessage(
            "Camera and microphone access is required. Please allow permissions in your browser settings and refresh the page."
          );
        } else {
          console.error("Error getting camera stream  -- ", err);
        }
      });
  }, [isCameraEnabled]);

  useEffect(() => {
    getCameraStream();
    return () => {
      // Use streamRef instead of videoRef.current — the DOM node may already
      // be null by the time this cleanup runs (React unmounts the element first).
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [getCameraStream]);

  const lessonStartLabel = lesson?.startDateTime
    ? new Date(lesson.startDateTime.toDate()).toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "TBD";

  if (loading) {
    return (
      <Box
        minHeight="100vh"
        minWidth="100vw"
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ bgcolor: "background.default" }}
      >
        <CircularProgress color="secondary" size={44} thickness={4} />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box
        minHeight="100vh"
        minWidth="100vw"
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
 bgcolor: "background.default",
px: 2 
}}
      >
        <Card
          variant="outlined"
          sx={{
            maxWidth: 440,
            width: "100%",
            textAlign: "center",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <WarningCircleIcon
            size={48}
            weight="fill"
            color={theme.palette.error.main}
            aria-hidden
          />
          <Typography variant="text20B" color="textPrimary">
            Something went wrong
          </Typography>
          <Typography variant="text16R" color="textSecondary">
            {errorMessage}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(TUTRED_CLIENT_HOSTNAME)}
            sx={{ mt: 1 }}
          >
            Back to Tutred
          </Button>
        </Card>
      </Box>
    );
  }

  const StatusIcon = statusVisual.Icon;

  return (
    <Box
      minHeight="100vh"
      minWidth="100vw"
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
 bgcolor: "background.default",
py: {
 xs: 2,
sm: 4 
},
px: 2 
}}
    >
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: {
 xs: "column",
lg: "row" 
},
          gap: {
 xs: 2,
md: 3 
},
          maxWidth: 1120,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: VIDEO_MAX_WIDTH,
              mx: {
 xs: "auto",
lg: 0 
},
              alignSelf: {
 xs: "center",
lg: "flex-start" 
},
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "4 / 3",
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "disabledGreen.main",
              }}
            >
              {isCameraEnabled && !permissionDeniedMessage ? (
                <Box
                  component="video"
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  sx={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    bgcolor: "grey.300",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    bgcolor: "background.paper",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "background.tertiary",
                      color: "text.secondary",
                    }}
                  >
                    <CameraSlashIcon size={40} weight="duotone" aria-hidden />
                  </Avatar>
                  <Typography
                    variant="text16R"
                    color="textSecondary"
                    sx={{
 mt: 2,
textAlign: "center" 
}}
                  >
                    {permissionDeniedMessage
                      ? permissionDeniedMessage
                      : "Camera is disabled"}
                  </Typography>
                </Box>
              )}
            </Box>
            <Stack direction="row" spacing={1} pt={2} justifyContent="center">
              <VideoControls
                  isMicEnabled={isMicEnabled}
                  isCameraEnabled={isCameraEnabled}
                  onToggleAudio={() => setIsMicEnabled(!isMicEnabled)}
                  onToggleVideo={() => setIsCameraEnabled(!isCameraEnabled)}
                />
          </Stack>
          </Box>
        </Box>

        <Stack
          sx={{
            width: {
 xs: "100%",
lg: 360 
},
            flexShrink: 0,
            justifyContent: "center",
            gap: 2,
            py: {
 xs: 0,
lg: 1 
},
          }}
        >
          {statusTitle && (
            <Stack direction="row" alignItems="center" gap={1}>
              <StatusIcon
                size={28}
                weight="duotone"
                color={statusVisual.color}
                aria-hidden
              />
              <Typography variant="text20B" color="secondary">
                {statusTitle}
              </Typography>
            </Stack>
          )}

          <Divider />

          <LessonDetailRow
            icon={ChalkboardTeacherIcon}
            label="Taught by"
            value={lesson?.tutorName}
            iconColor={theme.palette.secondary.main}
          />

          <LessonDetailRow
            icon={StudentIcon}
            label="Student"
            value={lesson?.studentName}
            iconColor={theme.palette.secondary.main}
          />

          <LessonDetailRow
            icon={CalendarBlankIcon}
            label="Scheduled time"
            value={lessonStartLabel}
            iconColor={theme.palette.secondary.main}
            valueVariant="text16B"
          />

          {enableJoin && (
            <Button
              variant="contained"
              color="primary"
              disabled={!enableJoin}
              onClick={handleJoin}
              fullWidth
              size="medium"
              sx={{ mt: 2 }}
            >
              Join lesson
            </Button>
          )}
        </Stack>
      </Card>
    </Box>
  );
};

export default Lobby;
